import React, { memo, useState, useMemo, useRef, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography,
  Chip,
  Skeleton,
} from '@mui/material';
import moment from 'moment';
import BookingDetailsDrawer from './BookingDetailsDrawer';
import BookingBar from './BookingBar';
import RoomMoveLinkOverlay from './RoomMoveLinkOverlay';
import DateHeader from './DateHeader';
import { 
  generateDateRange, 
  getTodayDateString,
  getHotelTimezone,
  isQuickBookDateAllowed,
  findDateColumn,
  findEndDateColumn,
  getGridBarMetrics,
} from '../../utils/dateUtils';
import { 
  formatDateString,
  createBookingKey,
  dateRangesOverlap,
  hasOverlappingBooking,
  isDuplicateBooking,
  getBookingStatusPriority,
  isBlockingBookingStatus,
  sortRoomsByNumber,
  resolveRoomTypeChangeKind,
  buildRoomTypeSortOrderMap,
  buildRoomTypeNameMap,
} from '../../utils/bookingUtils';
import {
  resolveCheckedOutBarBounds,
  resolveBookingRoomSegmentRoles,
  dayFractionFromMoment,
} from '../../utils/bookingStayDisplay';

/** Parse API/form clock string (HH:mm / HH:mm:ss) to a day fraction, or null. */
function timeStringToDayFraction(timeStr) {
  if (timeStr == null || timeStr === '') return null;
  if (moment.isMoment(timeStr)) {
    return timeStr.isValid() ? dayFractionFromMoment(timeStr) : null;
  }
  const parsed = moment(String(timeStr), ['HH:mm:ss', 'HH:mm'], true);
  return parsed.isValid() ? dayFractionFromMoment(parsed) : null;
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** Minimum visible slice when chaining mid-day / zero-width block geometry. */
const MIN_BLOCK_SLICE_MS = Math.round(0.12 * DAY_MS);

function isInventoryBlockBar(bar) {
  return Boolean(bar?.blockInfo) || String(bar?.status || '').toLowerCase() === 'blocked';
}

/** Absolute [start, end) ms for a block bar (hotel-day + clock fraction). */
function resolveBlockAbsRange(bar) {
  const startDay = moment(bar.startDate).startOf('day');
  const endExclusive = moment(bar.endDate).startOf('day');
  if (!startDay.isValid() || !endExclusive.isValid()) {
    return { absStart: 0, absEnd: 0, duration: 0 };
  }
  const lastDay = endExclusive.clone().subtract(1, 'day');
  const startF =
    bar.startDayFraction != null && Number.isFinite(Number(bar.startDayFraction))
      ? Number(bar.startDayFraction)
      : 0.5;
  const endF =
    bar.endDayFraction != null && Number.isFinite(Number(bar.endDayFraction))
      ? Number(bar.endDayFraction)
      : 0.5;
  let absStart = startDay.valueOf() + startF * DAY_MS;
  let absEnd = lastDay.valueOf() + endF * DAY_MS;
  if (absEnd <= absStart) {
    absEnd = absStart + MIN_BLOCK_SLICE_MS;
  }
  return { absStart, absEnd, duration: absEnd - absStart };
}

/**
 * Recompute calendar columns for a block bar after its absolute times change.
 * Returns false when the bar falls entirely outside the visible range.
 */
function applyAbsTimesToBlockBar(bar, absStart, absEnd, dateRange, calendarStartDate) {
  const startM = moment(absStart);
  const endM = moment(absEnd);
  if (!startM.isValid() || !endM.isValid() || !endM.isAfter(startM)) return false;

  const blockStartDate = startM.clone().startOf('day');
  const blockEndExclusive = endM.clone().startOf('day').add(1, 'day');
  const startsBeforeCalendar =
    calendarStartDate && blockStartDate.isBefore(calendarStartDate, 'day');

  const startCol = findDateColumn(dateRange, blockStartDate, startsBeforeCalendar);
  const { endCol, extendsBeyondCalendar } = findEndDateColumn(
    dateRange,
    blockEndExclusive,
    blockStartDate,
  );
  if (startCol < 0 || endCol < 0 || startCol > endCol) return false;

  bar.startDate = blockStartDate;
  bar.endDate = blockEndExclusive;
  bar.startDayFraction = dayFractionFromMoment(startM);
  bar.endDayFraction = dayFractionFromMoment(endM);
  bar.startCol = startCol;
  bar.span = endCol - startCol + 1;
  bar.startsBeforeCalendar = Boolean(startsBeforeCalendar);
  bar.extendsBeyondCalendar = Boolean(extendsBeyondCalendar);
  return true;
}

/**
 * Place overlapping inventory blocks end-to-end on the same room (no stacked override).
 * Later blocks start when the previous block ends; bars that cannot fit are dropped.
 */
function layoutBlockedBarsSequentially(roomBars, dateRange, calendarStartDate) {
  if (!Array.isArray(roomBars) || roomBars.length < 2) return;

  const blockEntries = [];
  roomBars.forEach((bar, index) => {
    if (!isInventoryBlockBar(bar)) return;
    blockEntries.push({ index, bar, ...resolveBlockAbsRange(bar) });
  });
  if (blockEntries.length < 2) return;

  blockEntries.sort(
    (a, b) =>
      a.absStart - b.absStart ||
      a.absEnd - b.absEnd ||
      (Number(a.bar.blockInfo?.id) || 0) - (Number(b.bar.blockInfo?.id) || 0) ||
      a.index - b.index,
  );

  let cursor = null;
  const dropIndexes = new Set();
  blockEntries.forEach((item) => {
    let absStart = item.absStart;
    let absEnd = item.absEnd;
    const duration = Math.max(item.duration, MIN_BLOCK_SLICE_MS);
    if (cursor != null && absStart < cursor) {
      absStart = cursor;
      absEnd = absStart + duration;
    }
    if (absEnd <= absStart) {
      dropIndexes.add(item.index);
      return;
    }
    if (!applyAbsTimesToBlockBar(item.bar, absStart, absEnd, dateRange, calendarStartDate)) {
      dropIndexes.add(item.index);
      return;
    }
    cursor = absEnd;
  });

  if (dropIndexes.size === 0) return;
  for (let i = roomBars.length - 1; i >= 0; i -= 1) {
    if (dropIndexes.has(i)) roomBars.splice(i, 1);
  }
}

/** Merge item-level room-segment timestamps onto a calendar bar entry. */
function mergeSegmentTimestampsIntoEntry(entry, item) {
  if (!entry || !item) return;
  const rawStart = item.segment_started_at || item.room_changed_at;
  const rawEnd = item.segment_ended_at;
  if (rawStart) {
    const m = moment.utc(rawStart);
    if (m.isValid()) {
      if (!entry.segmentStartedAt || m.isBefore(entry.segmentStartedAt)) {
        entry.segmentStartedAt = m.clone();
      }
    }
  }
  if (rawEnd) {
    const m = moment.utc(rawEnd);
    if (m.isValid()) {
      if (!entry.segmentEndedAt || m.isAfter(entry.segmentEndedAt)) {
        entry.segmentEndedAt = m.clone();
      }
    }
  }
  if (item.room_changed_at) {
    entry.hasRoomChange = true;
    const changed = moment.utc(item.room_changed_at);
    if (changed.isValid()) {
      if (!entry.roomChangedAt || changed.isBefore(entry.roomChangedAt)) {
        entry.roomChangedAt = changed.clone();
      }
    }
  }
  if (item.upgraded_from_room_type_id) {
    entry.hasRoomUpgrade = true;
    entry.upgradedFromRoomTypeId = item.upgraded_from_room_type_id;
  }
}

/** Resolve display room number from id map. */
function roomNumberLabel(roomNumberById, roomId) {
  const idNum = Number(roomId);
  if (!Number.isFinite(idNum) || idNum <= 0) return null;
  return roomNumberById[idNum] || String(idNum);
}

/**
 * True when room_segments describe a sequential move (102 then 301), not a
 * concurrent multi-room booking (202 + 203 on the same nights).
 */
function segmentsIndicateSequentialRoomChange(segments) {
  if (!Array.isArray(segments) || segments.length < 2) return false;
  const normalized = segments
    .filter((s) => s?.start_date && s?.end_date && Number(s?.room_id) > 0)
    .map((s) => ({
      roomId: Number(s.room_id),
      start: String(s.start_date),
      end: String(s.end_date),
      changedAt: s.room_changed_at || null,
    }));
  if (normalized.length < 2) return false;

  // Explicit room-change timestamps always count as a move.
  if (normalized.some((s) => s.changedAt)) return true;

  // Same start + end on every segment → multi-room stay, not a room change.
  const first = normalized[0];
  const allSameWindow = normalized.every(
    (s) => s.start === first.start && s.end === first.end,
  );
  if (allSameWindow) return false;

  // Sequential move: a later segment starts on/after an earlier segment's end
  // (guest left room A then occupied room B). Overlapping same-stay windows stay multi-room.
  const byStart = [...normalized].sort((a, b) => a.start.localeCompare(b.start));
  for (let i = 0; i < byStart.length - 1; i += 1) {
    const a = byStart[i];
    const b = byStart[i + 1];
    if (a.roomId === b.roomId) continue;
    if (b.start >= a.end) return true;
  }
  return false;
}

/**
 * Room-change / upgrade summary for bar tooltips & icons.
 * from = previous segment room, to = next segment room (not the current bar room).
 * Example: bar on 102 with move to 301 → from=null, current=102, to=301 → "Moved to Room 301".
 */
function buildBarRoomMoveMeta(
  listBooking,
  roomId,
  roomNumberById,
  entry,
  sortOrderByTypeId = {},
  roomTypeIdByRoomId = {},
  roomTypeNameById = {},
) {
  const rid = Number(roomId);
  const segments = Array.isArray(listBooking?.room_segments)
    ? [...listBooking.room_segments]
    : [];
  segments.sort((a, b) => String(a?.start_date || '').localeCompare(String(b?.start_date || '')));
  const idx = segments.findIndex((s) => Number(s?.room_id) === rid);
  const mine = idx >= 0 ? segments[idx] : null;
  // Only walk previous/next among sequential room-change segments.
  const isSequentialChange = segmentsIndicateSequentialRoomChange(segments);
  const prev = isSequentialChange && idx > 0 ? segments[idx - 1] : null;
  const next = isSequentialChange && idx >= 0 && idx < segments.length - 1
    ? segments[idx + 1]
    : null;
  const hasRoomChange = Boolean(
    isSequentialChange
    || mine?.room_changed_at
    || entry?.hasRoomChange
    || entry?.roomChangedAt,
  );
  const fromTypeId = Number(
    mine?.upgraded_from_room_type_id
    || entry?.upgradedFromRoomTypeId
    || prev?.room_type_id
    || 0,
  ) || 0;
  const toTypeId = Number(
    mine?.room_type_id
    || roomTypeIdByRoomId[rid]
    || entry?.room_type_id
    || 0,
  ) || 0;
  const typeName = (typeId) => {
    const id = Number(typeId) || 0;
    if (!id) return null;
    return roomTypeNameById[id] || roomTypeNameById[String(id)] || null;
  };
  const hasRoomUpgrade = Boolean(
    entry?.hasRoomUpgrade
    || entry?.upgradedFromRoomTypeId
    || mine?.upgraded_from_room_type_id
    || (fromTypeId && toTypeId && fromTypeId !== toTypeId && isSequentialChange),
  );
  if (!hasRoomChange && !hasRoomUpgrade) return null;
  const roomTypeChangeKind = hasRoomUpgrade
    ? resolveRoomTypeChangeKind(fromTypeId, toTypeId, sortOrderByTypeId)
    : 'upgrade';
  const currentRoomNumber = roomNumberLabel(roomNumberById, rid);
  const fromRoomNumber = prev ? roomNumberLabel(roomNumberById, prev.room_id) : null;
  const toRoomNumber = next ? roomNumberLabel(roomNumberById, next.room_id) : null;
  return {
    hasRoomChange,
    hasRoomUpgrade,
    roomTypeChangeKind,
    fromRoomTypeId: fromTypeId || null,
    toRoomTypeId: toTypeId || null,
    fromRoomTypeName: typeName(fromTypeId),
    toRoomTypeName: typeName(toTypeId),
    currentRoomNumber,
    fromRoomNumber,
    toRoomNumber,
    fromRoomId: prev ? Number(prev.room_id) || null : null,
    toRoomId: next ? Number(next.room_id) || null : null,
    currentRoomId: rid || null,
    bookingId: Number(listBooking?.id || listBooking?.booking_id || 0) || null,
    changedAt: mine?.room_changed_at || entry?.roomChangedAt || null,
    segmentStartedAt:
      mine?.segment_started_at
      || entry?.segmentStartedAt?.toISOString?.()
      || entry?.segmentStartedAt
      || null,
    segmentEndedAt:
      mine?.segment_ended_at
      || entry?.segmentEndedAt?.toISOString?.()
      || entry?.segmentEndedAt
      || null,
  };
}
import { groupRooms, getRoomsByType } from '../../utils/roomUtils';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';
import { alpha } from '@mui/material/styles';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import MaintenanceRoomIndicator from '../housekeeping/MaintenanceRoomIndicator';
import { resolveRoomMaintenanceSummary } from '../../utils/maintenanceRoomUtils';
import {
  getRoomMaintenanceBlockLabel,
  isRoomMaintenanceBlockedOn,
} from '../../utils/roomMaintenanceBlock';
import {
  formatRoomChipLabel,
  getGridDateHeaderRowSx,
  getGridDayCellSx,
  getGridLineColor,
  getGridRoomLabelColumnSx,
  getGridRoomNumberBadgeSx,
  getGridRoomsColumnHeaderSx,
  getGridRoomsColumnLabelSx,
  getGridRowSx,
  getGridSectionCountChipSx,
  getGridSectionHeaderSx,
  getGridSectionPaperSx,
  getGridSectionTitleSx,
} from '../../utils/dashboardGridCalendarLayout';
import { calendarDataBoneSx } from '../../utils/dashboardCalendarLoading';
import RoomBoardInventoryStats, {
  summarizeRoomBoardFromGridBarsRange,
} from '../../utils/roomBoardInventorySummary';

/** Bar height and gap for multi-line stacking (px); logo sits inside bar (no vertical overlap between lanes) */
const BAR_HEIGHT = 44;
const BAR_GAP = 8;
const BAR_TOP_OFFSET = 8;
const MOBILE_BAR_HEIGHT = 24;
const MOBILE_BAR_GAP = 3;
const MOBILE_BAR_TOP_OFFSET = 4;
const MOBILE_ROOM_LABEL_WIDTH = 58;

/**
 * Assign lane index to each bar so overlapping bars are on different horizontal lines.
 * Bars are ordered by startCol then endCol; each bar gets the first lane where it doesn't overlap.
 * @param {Array} bars - Array of bar objects with startCol and span
 * @returns {Array<number>} Array of lane indices (0, 1, 2, ...) same length as bars
 */
/** True when a blocking stay occupies this date column for the room (quick book disabled). */
function dateColumnBlocksQuickBook(roomBars, colIndex) {
  if (!roomBars?.length) return false;
  return roomBars.some((bar) => {
    const span = bar.span || 1;
    const end = bar.startCol + span - 1;
    if (colIndex < bar.startCol || colIndex > end) return false;
    // Bar visually includes the checkout column, but stay nights are [check_in, check_out).
    // Allow quick book on the checkout date (new guest arrives when the previous departs).
    if (colIndex === end) return false;
    return isBlockingBookingStatus(bar.status) && bar.blocksRoomForAllocation !== false;
  });
}

/**
 * Stay-night overlap for lane packing: [checkIn, checkOut) half-open.
 * 1st→2nd and 2nd→3rd only meet on turnover day → no overlap (same row).
 * Shared nights / between-date overlap → true (next row).
 */
function stayNightsOverlap(startA, endA, startB, endB) {
  const aStart = moment(startA).startOf('day');
  const aEnd = moment(endA).startOf('day');
  const bStart = moment(startB).startOf('day');
  const bEnd = moment(endB).startOf('day');
  if (!aStart.isValid() || !aEnd.isValid() || !bStart.isValid() || !bEnd.isValid()) {
    return false;
  }
  return aStart.isBefore(bEnd, 'day') && bStart.isBefore(aEnd, 'day');
}

/** Same-day turnover: 9am checkout then 4pm check-in share one lane (no stack). */
function barsOccupySameLane(a, b) {
  if (!stayNightsOverlap(a.startDate, a.endDate, b.startDate, b.endDate)) {
    return true; // no night overlap → same lane OK
  }
  const aStart = moment(a.startDate).startOf('day');
  const aEnd = moment(a.endDate).startOf('day');
  const bStart = moment(b.startDate).startOf('day');
  const bEnd = moment(b.endDate).startOf('day');
  // Meet on checkout/check-in day only: use clock fractions when present.
  const aEndsOnBStart = aEnd.isSame(bStart, 'day') && aStart.isBefore(bStart, 'day');
  const bEndsOnAStart = bEnd.isSame(aStart, 'day') && bStart.isBefore(aStart, 'day');
  if (aEndsOnBStart || bEndsOnAStart) {
    const early = aEndsOnBStart ? a : b;
    const late = aEndsOnBStart ? b : a;
    const endF = early.endDayFraction != null && Number.isFinite(Number(early.endDayFraction))
      ? Number(early.endDayFraction)
      : 0.5;
    const startF = late.startDayFraction != null && Number.isFinite(Number(late.startDayFraction))
      ? Number(late.startDayFraction)
      : 0.5;
    if (endF <= startF + 0.02) return true;
  }
  // Same stay-night window (e.g. morning checkout + afternoon inventory block):
  // share a lane when clock intervals do not overlap.
  const aStartF = a.startDayFraction != null && Number.isFinite(Number(a.startDayFraction))
    ? Number(a.startDayFraction)
    : 0.5;
  const aEndF = a.endDayFraction != null && Number.isFinite(Number(a.endDayFraction))
    ? Number(a.endDayFraction)
    : 0.5;
  const bStartF = b.startDayFraction != null && Number.isFinite(Number(b.startDayFraction))
    ? Number(b.startDayFraction)
    : 0.5;
  const bEndF = b.endDayFraction != null && Number.isFinite(Number(b.endDayFraction))
    ? Number(b.endDayFraction)
    : 0.5;
  const sameSingleNight =
    aStart.isSame(bStart, 'day') &&
    aEnd.isSame(bEnd, 'day') &&
    aEnd.diff(aStart, 'days') === 1;
  if (sameSingleNight) {
    const aBeforeB = aEndF <= bStartF + 0.02;
    const bBeforeA = bEndF <= aStartF + 0.02;
    // Both "before" ⇒ identical mid-point windows — must not share a lane (ghost stack).
    if (aBeforeB && bBeforeA) return false;
    if (aBeforeB || bBeforeA) return true;
  }
  // Inventory blocks: only share a lane when clock windows do not overlap.
  if (isInventoryBlockBar(a) && isInventoryBlockBar(b)) {
    const aRange = resolveBlockAbsRange(a);
    const bRange = resolveBlockAbsRange(b);
    if (aRange.absEnd <= bRange.absStart + 60 * 1000) return true;
    if (bRange.absEnd <= aRange.absStart + 60 * 1000) return true;
    return false;
  }
  // Multi-day edge: checkout/block meet on the shared day with non-overlapping clocks.
  if (isInventoryBlockBar(a) || isInventoryBlockBar(b)) {
    if (aEndF <= bStartF + 0.02 && aEnd.isSame(bStart, 'day') && !(bEndF <= aStartF + 0.02)) {
      return true;
    }
    if (bEndF <= aStartF + 0.02 && bEnd.isSame(aStart, 'day') && !(aEndF <= bStartF + 0.02)) {
      return true;
    }
  }
  return false;
}

function assignBarLanes(bars) {
  if (!bars || bars.length === 0) return [];
  const sorted = [...bars].map((b, i) => ({ ...b, _idx: i }));
  // Prefer higher-priority stays on earlier lanes so cancelled/no-show drop to the next row
  // when they truly overlap an active booking.
  sorted.sort((a, b) => {
    const aStart = moment(a.startDate).valueOf();
    const bStart = moment(b.startDate).valueOf();
    if (aStart !== bStart) return aStart - bStart;
    const priorityDelta =
      getBookingStatusPriority(b.status) - getBookingStatusPriority(a.status);
    if (priorityDelta !== 0) return priorityDelta;
    return moment(a.endDate).valueOf() - moment(b.endDate).valueOf();
  });
  const lanes = sorted.map(() => 0);
  const laneBars = []; // laneBars[lane] = [bar, ...]

  sorted.forEach((bar) => {
    let lane = 0;
    while (lane < laneBars.length) {
      const overlaps = laneBars[lane].some((other) => !barsOccupySameLane(bar, other));
      if (!overlaps) break;
      lane++;
    }
    if (lane === laneBars.length) laneBars.push([]);
    laneBars[lane].push(bar);
    lanes[bar._idx] = lane;
  });

  return lanes;
}

/** Align API / UI variants: "Partial Check-Out", "confirmed", etc. */
function normalizeCalendarBookingStatus(raw) {
  return String(raw ?? 'booked')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
}

/** Cancelled / no-show must not paint room-board line bars. */
function isHiddenFromCalendarBars(status) {
  const s = normalizeCalendarBookingStatus(status);
  return s === 'cancelled' || s === 'canceled' || s === 'no_show';
}

const IN_HOUSE_BOOKING_STATUSES = new Set(['checked_in', 'partial_checkin', 'partial_checkout']);

/**
 * Merge BookingItem check-in flags onto a room-stream bar entry.
 * Used so Ultra bars match Grid cards: green only when this room is checked in.
 */
function mergeItemCheckinIntoEntry(entry, item) {
  if (!entry || !item) return;
  const hasSignal =
    item.checked_in != null
    || item.checkout === true
    || (item.item_status != null && String(item.item_status).trim() !== '');
  if (!hasSignal) return;

  entry.hasItemLevelCheckinInfo = true;
  if (item.checked_in === true || item.checked_in === 1 || item.checked_in === 'true') {
    entry.checked_in = true;
  } else if (entry.checked_in == null && (item.checked_in === false || item.checked_in === 0)) {
    entry.checked_in = false;
  }
  if (item.checkout === true) {
    entry.checkout = true;
  }
  const itemStatus = normalizeCalendarBookingStatus(item.item_status || '');
  if (itemStatus === 'checked_in' || entry.checked_in === true) {
    entry.item_status = 'checked_in';
  } else if (itemStatus === 'checked_out' || (item.checkout === true && entry.item_status !== 'checked_in')) {
    if (entry.item_status !== 'checked_in') {
      entry.item_status = 'checked_out';
    }
  } else if (itemStatus && entry.item_status !== 'checked_in') {
    entry.item_status = itemStatus;
  } else if (!entry.item_status) {
    entry.item_status = 'confirmed';
  }
}

/**
 * Bar paint status for one physical room stream.
 * Prefer item_status / checked_in (Grid card parity); never paint every room green from
 * booking-level partial_checkin / checked_in rollup.
 * Fully checked-out bookings are always Out on stay nights (date-range inventory).
 */
function resolveRoomBarStatus(bookingInfo, listStatus) {
  const bookingStatus = normalizeCalendarBookingStatus(listStatus || bookingInfo?.status);

  // Booking fully departed: historical item checked_in flags must not keep nights as In House.
  if (bookingStatus === 'checked_out') {
    return 'checked_out';
  }

  if (bookingStatus === 'partial_checkout' && bookingInfo?.blocksRoomForAllocation === false) {
    return 'checked_out';
  }
  if (bookingInfo?.checkout === true && bookingInfo?.checked_in !== true) {
    return 'checked_out';
  }

  const itemStatus = normalizeCalendarBookingStatus(bookingInfo?.item_status || '');
  if (itemStatus === 'checked_out') return 'checked_out';
  if (itemStatus === 'checked_in' || bookingInfo?.checked_in === true) return 'checked_in';

  const hasItemInfo =
    bookingInfo?.hasItemLevelCheckinInfo === true
    || bookingInfo?.item_status != null
    || bookingInfo?.checked_in != null;

  if (hasItemInfo && IN_HOUSE_BOOKING_STATUSES.has(bookingStatus)) {
    // Allocated but not yet checked in on this room → booked (orange), same as Grid.
    return 'confirmed';
  }

  return bookingStatus;
}

/**
 * After nightly rows are merged, sync booking-level status from the top-level `bookings`
 * payload onto each entry. Room-bar paint still uses resolveRoomBarStatus (item check-in).
 */
function applyBookingListStatusToMap(bookingMap, bookings) {
  if (!bookings?.length) return;
  const byId = new Map();
  bookings.forEach((b) => {
    const id = Number(b?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    byId.set(id, b);
  });
  Object.values(bookingMap).forEach((entry) => {
    const idNum = Number(entry.booking?.id);
    if (!Number.isFinite(idNum)) return;
    const listB = byId.get(idNum);
    if (!listB) return;
    const st = normalizeCalendarBookingStatus(listB.status ?? entry.status);
    entry.status = st;
    entry.booking = { ...entry.booking, status: st };
    // Keep booking-level color only when this room stream has no item check-in signal.
    if (listB.status_color && !entry.hasItemLevelCheckinInfo) {
      entry.status_color = listB.status_color;
    }
  });
}

/**
 * Phase B — Top-level API `bookings` list: add any reservation overlapping the grid that does not
 * already have an `item_room_${id}_${roomId}` row from nightly items (confirmed-only-on-list, etc.).
 *
 * Prefer `room_segments` (per-room date windows) so room-change history is not painted with the
 * full booking span — that caused duplicate flush-left bars when the calendar started mid-stay.
 */
function mergeBookingsApiIntoBookingMap(bookingMap, bookings, dateRange, bookingMetaById) {
  if (!bookings?.length || !dateRange?.length) return;

  const rangeStart = moment(dateRange[0].key).startOf('day');
  const rangeEnd = moment(dateRange[dateRange.length - 1].key).startOf('day');

  bookings.forEach((booking) => {
    if (!booking.id || !booking.check_in_date || !booking.check_out_date) return;

    const checkIn = moment(booking.check_in_date).startOf('day');
    const checkOut = moment(booking.check_out_date).startOf('day');
    if (checkOut.isBefore(rangeStart, 'day') || checkIn.isAfter(rangeEnd, 'day')) return;

    const idNum = Number(booking.id);
    if (!Number.isFinite(idNum) || idNum <= 0) return;

    const status = normalizeCalendarBookingStatus(booking.status);
    if (isHiddenFromCalendarBars(status)) return;
    const meta = bookingMetaById[idNum] || {};

    const roomIdsFromApi = [];
    if (Array.isArray(booking.room_ids) && booking.room_ids.length > 0) {
      roomIdsFromApi.push(...booking.room_ids);
    } else if (booking.room_id != null && booking.room_id !== '') {
      roomIdsFromApi.push(booking.room_id);
    }

    const baseBooking = {
      id: booking.id,
      guest_name: booking.guest_name || booking.guestName || booking.title || 'Guest',
      booking_code: booking.code || booking.booking_code || '',
      status,
      booking_source: booking.booking_source || 'direct',
      room_type_id: booking.room_type_id || null,
      check_in_date: booking.check_in_date || null,
      check_out_date: booking.check_out_date || null,
      scheduled_check_out_date: booking.scheduled_check_out_date || booking.check_out_date || null,
      check_in_time: booking.check_in_time || meta.check_in_time || null,
      check_out_time: booking.check_out_time || meta.check_out_time || null,
      hotel_check_in_time: booking.hotel_check_in_time || meta.hotel_check_in_time || null,
      hotel_check_out_time: booking.hotel_check_out_time || meta.hotel_check_out_time || null,
      hotel_timezone: booking.hotel_timezone || meta.hotel_timezone || null,
      channel_image_path: booking.channel_image_path || meta.channel_image_path || null,
      channel_name: booking.channel_name || meta.channel_name || null,
      group_booking_id: booking.group_booking_id || meta.group_booking_id || null,
      group_booking_code: booking.group_booking_code || meta.group_booking_code || null,
      is_group_member: Boolean(
        booking.is_group_member
        || booking.group_booking_id
        || meta.is_group_member
        || meta.group_booking_id
      ),
    };

    const segments = Array.isArray(booking.room_segments) ? booking.room_segments : [];
    if (segments.length > 0) {
      segments.forEach((seg) => {
        const roomIdNum = parseInt(seg?.room_id, 10);
        if (!Number.isFinite(roomIdNum) || roomIdNum <= 0) return;
        if (!seg?.start_date || !seg?.end_date) return;
        const segStart = moment(seg.start_date).startOf('day');
        const segEnd = moment(seg.end_date).startOf('day');
        if (!segStart.isValid() || !segEnd.isValid()) return;
        // Inclusive visual checkout day: overlap if segment ends on/after range start.
        if (segEnd.isBefore(rangeStart, 'day') || segStart.isAfter(rangeEnd, 'day')) return;

        const bookingKey = `item_room_${booking.id}_${roomIdNum}`;
        if (bookingMap[bookingKey]) {
          mergeSegmentTimestampsIntoEntry(bookingMap[bookingKey], seg);
          mergeItemCheckinIntoEntry(bookingMap[bookingKey], {
            checked_in: seg.checked_in,
            checkout: seg.checkout,
            item_status: seg.item_status,
          });
          if (seg.checkout === true) {
            bookingMap[bookingKey].blocksRoomForAllocation = false;
          }
          // Checked-out: keep bar through booking/segment end for date-range Out nights.
          if (status === 'checked_out') {
            if (segEnd.isAfter(bookingMap[bookingKey].checkOut, 'day')) {
              bookingMap[bookingKey].checkOut = segEnd.clone();
            }
            if (checkOut.isAfter(bookingMap[bookingKey].checkOut, 'day')) {
              bookingMap[bookingKey].checkOut = checkOut.clone();
            }
          }
          return;
        }
        const entry = {
          booking: { ...baseBooking },
          roomId: roomIdNum,
          checkIn: segStart.clone(),
          checkOut: segEnd.clone(),
          status,
          status_color: booking.status_color || meta.status_color || 'orange',
          blocksRoomForAllocation: seg.checkout !== true,
          segmentStartedAt: null,
          segmentEndedAt: null,
        };
        mergeSegmentTimestampsIntoEntry(entry, seg);
        mergeItemCheckinIntoEntry(entry, {
          checked_in: seg.checked_in,
          checkout: seg.checkout,
          item_status: seg.item_status,
        });
        bookingMap[bookingKey] = entry;
      });
    } else if (roomIdsFromApi.length > 0) {
      // Legacy: never invent full-span bars for rooms when this booking already has
      // item-level rows (room-change nights outside the visible range would duplicate).
      const hasItemRowsForBooking = Object.keys(bookingMap).some((key) =>
        key.startsWith(`item_room_${booking.id}_`),
      );
      if (!hasItemRowsForBooking) {
        roomIdsFromApi.forEach((rid) => {
          const roomIdNum = parseInt(rid, 10);
          if (!Number.isFinite(roomIdNum)) return;
          const bookingKey = `item_room_${booking.id}_${roomIdNum}`;
          if (bookingMap[bookingKey]) return;
          bookingMap[bookingKey] = {
            booking: { ...baseBooking },
            roomId: roomIdNum,
            checkIn: checkIn.clone(),
            checkOut: checkOut.clone(),
            status,
            status_color: booking.status_color || meta.status_color || 'orange',
            blocksRoomForAllocation: status !== 'checked_out',
          };
        });
      } else if (status === 'checked_out') {
        // Item rows exist but may be truncated after checkout — extend each room stream to CO.
        roomIdsFromApi.forEach((rid) => {
          const roomIdNum = parseInt(rid, 10);
          if (!Number.isFinite(roomIdNum)) return;
          const bookingKey = `item_room_${booking.id}_${roomIdNum}`;
          const entry = bookingMap[bookingKey];
          if (!entry) return;
          if (checkOut.isAfter(entry.checkOut, 'day')) {
            entry.checkOut = checkOut.clone();
          }
          entry.status = 'checked_out';
          entry.booking = { ...entry.booking, status: 'checked_out' };
          entry.blocksRoomForAllocation = false;
        });
      }
    }

    /** Partial checkout: rooms omitted from room_ids / nightly map — anchor gray bars here. */
    const checkedOutRaw = Array.isArray(booking.checked_out_room_ids) ? booking.checked_out_room_ids : [];
    const checkedOutRoomIds = [
      ...new Set(
        checkedOutRaw
          .map((rid) => parseInt(rid, 10))
          .filter((n) => Number.isFinite(n) && n > 0),
      ),
    ];
    checkedOutRoomIds.forEach((roomIdNum) => {
      const bookingKey = `item_room_${booking.id}_${roomIdNum}`;
      if (bookingMap[bookingKey]) {
        bookingMap[bookingKey].blocksRoomForAllocation = false;
        return;
      }
      // Prefer segment dates when available (avoid full-booking phantom bars).
      const seg = segments.find((s) => Number(s?.room_id) === roomIdNum);
      if (seg?.start_date && seg?.end_date) {
        const segStart = moment(seg.start_date).startOf('day');
        const segEnd = moment(seg.end_date).startOf('day');
        if (segEnd.isBefore(rangeStart, 'day') || segStart.isAfter(rangeEnd, 'day')) return;
        const entry = {
          booking: { ...baseBooking },
          roomId: roomIdNum,
          checkIn: segStart.clone(),
          checkOut: segEnd.clone(),
          status,
          status_color: booking.status_color || meta.status_color || 'orange',
          blocksRoomForAllocation: false,
          segmentStartedAt: null,
          segmentEndedAt: null,
        };
        mergeSegmentTimestampsIntoEntry(entry, seg);
        bookingMap[bookingKey] = entry;
        return;
      }
      const hasItemRowsForBooking = Object.keys(bookingMap).some((key) =>
        key.startsWith(`item_room_${booking.id}_`),
      );
      if (hasItemRowsForBooking) return;
      bookingMap[bookingKey] = {
        booking: { ...baseBooking },
        roomId: roomIdNum,
        checkIn: checkIn.clone(),
        checkOut: checkOut.clone(),
        status,
        status_color: booking.status_color || meta.status_color || 'orange',
        blocksRoomForAllocation: false,
      };
    });

    const metaObj = booking.meta && typeof booking.meta === 'object' ? booking.meta : null;
    const rtFromMeta = metaObj
      ? parseInt(
          metaObj.room_type_id ?? metaObj.primary_room_type_id ?? metaObj.default_room_type_id ?? '',
          10,
        )
      : NaN;
    const rtid =
      booking.room_type_id ||
      (Array.isArray(booking.room_type_ids) && booking.room_type_ids.length > 0
        ? booking.room_type_ids[0]
        : null) ||
      (Number.isFinite(rtFromMeta) && rtFromMeta > 0 ? rtFromMeta : null);

    // room_segments already enumerate every physical stay (including room changes).
    // Do NOT invent unallocated fallbacks from room_ids.length — that list is historical
    // rooms across a move (102→301), not concurrent rooms. When the calendar starts mid-stay
    // only the later segment is in range, streamsNow=1 while room_ids=2, and the fallback
    // painted a full-span phantom on the first free Deluxe room (e.g. 101).
    if (segments.length > 0) return;

    const streamsNow = Object.values(bookingMap).filter((v) => Number(v.booking?.id) === idNum).length;
    const totalRooms = Math.max(
      roomIdsFromApi.length + checkedOutRoomIds.length,
      parseInt(booking.total_rooms, 10) || 1,
    );

    const rtidNum = rtid ? parseInt(rtid, 10) || rtid : null;
    for (let idx = streamsNow; idx < totalRooms && rtidNum; idx++) {
      const bookingKey = `fallback_unallocated_${booking.id}_extra_${idx}`;
      if (bookingMap[bookingKey]) continue;
      bookingMap[bookingKey] = {
        booking: {
          ...baseBooking,
          room_type_id: rtidNum,
        },
        roomId: null,
        checkIn: checkIn.clone(),
        checkOut: checkOut.clone(),
        status,
        status_color: booking.status_color || meta.status_color || 'orange',
        blocksRoomForAllocation: true,
      };
    }
  });
}

/**
 * Booking Calendar Grid View Component
 * Displays a Material Design calendar grid matching the reference design
 * Shows booking bars spanning from check-in to check-out dates
 */
const BookingCalendarGridView = ({
  data,
  theme = 'light',
  groupView = 'full',
  onQuickBook,
  onCheckout,
  onCheckIn,
  onOpenWebCheckInInvite,
  layoutVariant = 'desktop',
  gridDayCount,
  isLoading = false,
}) => {
  const { 
    rooms = [], 
    room_types = [], 
    bookings = [], 
    booking_items_by_date = {},
    blocked_rooms_by_date = {},
    start_date, 
    end_date,
    hotel_check_in_time: calendarHotelCheckInTime = null,
    hotel_check_out_time: calendarHotelCheckOutTime = null,
  } = data || {};

  const isDark = theme === 'dark';
  const isMobileLayout = layoutVariant === 'mobile';
  const isWorkspaceLayout = layoutVariant === 'workspace';
  const useWorkspaceGrid = isWorkspaceLayout && !isMobileLayout;
  const hotelTimezone = getHotelTimezone();
  const roomLabelWidth = isMobileLayout ? MOBILE_ROOM_LABEL_WIDTH : useWorkspaceGrid ? 108 : 120;
  const mobileGridColumns = (dayCount) =>
    `${roomLabelWidth}px repeat(${dayCount}, minmax(0, 1fr))`;
  const sectionPaperSx = isMobileLayout
    ? {
        mb: 1.5,
        background: 'linear-gradient(155deg, rgba(0,0,0,0.72) 0%, rgba(3,7,18,0.88) 100%)',
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: '16px',
        overflow: 'visible',
        boxShadow: '0 10px 24px rgba(0,0,0,0.35)',
      }
    : useWorkspaceGrid
      ? getGridSectionPaperSx(theme)
      : {
        mb: 3,
        background: isDark
          ? 'linear-gradient(135deg, #2a2a31 0%, #1e1e24 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(0, 0, 0, 0.12)'}`,
        borderRadius: '16px',
        overflow: 'visible',
        position: 'relative',
        boxShadow: isDark
          ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(76, 225, 182, 0.1)'
          : '0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      };
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  
  // Generate date range using utility; keep workspace grid aligned to selected day count.
  const dateRange = useMemo(() => {
    const range = generateDateRange(start_date, end_date);
    if (
      useWorkspaceGrid
      && gridDayCount
      && start_date
      && range.length !== gridDayCount
    ) {
      const alignedEnd = moment(start_date, 'YYYY-MM-DD', true)
        .add(gridDayCount - 1, 'days')
        .format('YYYY-MM-DD');
      return generateDateRange(start_date, alignedEnd);
    }
    return range;
  }, [start_date, end_date, useWorkspaceGrid, gridDayCount]);
  const effectiveDayCount = useWorkspaceGrid && gridDayCount ? gridDayCount : dateRange.length;
  const gridBarMetrics = useMemo(
    () => (useWorkspaceGrid ? getGridBarMetrics(effectiveDayCount) : null),
    [useWorkspaceGrid, effectiveDayCount],
  );
  const barHeight = isMobileLayout
    ? MOBILE_BAR_HEIGHT
    : (gridBarMetrics?.barHeight ?? (useWorkspaceGrid ? 48 : BAR_HEIGHT));
  const barGap = isMobileLayout
    ? MOBILE_BAR_GAP
    : (gridBarMetrics?.barGap ?? (useWorkspaceGrid ? 6 : BAR_GAP));
  const barTopOffset = isMobileLayout
    ? MOBILE_BAR_TOP_OFFSET
    : (gridBarMetrics?.barTopOffset ?? (useWorkspaceGrid ? 10 : BAR_TOP_OFFSET));

  const mobileDateRangeLabel = useMemo(() => {
    if (!isMobileLayout || dateRange.length === 0) return '';
    const first = dateRange[0];
    const last = dateRange[dateRange.length - 1];
    const firstDay = parseInt(first.dayNum, 10);
    const lastDay = parseInt(last.dayNum, 10);
    if (first.month === last.month && first.year === last.year) {
      return `${first.month} ${firstDay} – ${lastDay}, ${first.year}`;
    }
    return `${first.month} ${firstDay} – ${last.month} ${lastDay}`;
  }, [isMobileLayout, dateRange]);

  // Get current date for highlighting today's date
  const currentDateStr = useMemo(() => {
    return getTodayDateString();
  }, []);

  // Build grid bars using three explicit passes:
  // Phase A — booking_items_by_date: nightly rows merged into one bar per booking + room (all statuses).
  // Phase B — bookings API list: mergeBookingsApiIntoBookingMap (list-only / gap-fill; same keys as A).
  // Phase C — legacy seed when the API sends no daily map at all (booking_* keys → same downstream pipeline).
  const calendarGrid = useMemo(() => {
    const bookingBars = {}; // {roomId: [{booking, startDate, endDate, startCol, span, status}]}
    const hasItemLevelMap = booking_items_by_date && Object.keys(booking_items_by_date).length > 0;

    const bookingMap = {}; // {bookingKey: {booking, roomId, checkIn, checkOut, status}}
    // Scheduled dates + booking-level status (one bar per booking/room; do not split on item_status).
    const scheduledCheckOutByBookingId = {};
    const bookingMetaById = {};
    if (bookings && bookings.length > 0) {
      bookings.forEach((b) => {
        const idNum = Number(b.id);
        if (!Number.isFinite(idNum) || idNum <= 0) return;
        if (b.check_out_date) {
          scheduledCheckOutByBookingId[idNum] = moment(b.check_out_date);
        }
        bookingMetaById[idNum] = {
          status: normalizeCalendarBookingStatus(b.status),
          status_color: b.status_color || 'orange',
          check_in_date: b.check_in_date ? moment(b.check_in_date) : null,
          check_out_date: b.check_out_date ? moment(b.check_out_date) : null,
          scheduled_check_out_date: b.scheduled_check_out_date || b.check_out_date || null,
          check_in_time: b.check_in_time || b.actual_checkin_time || null,
          check_out_time: b.check_out_time || b.actual_checkout_time || null,
          hotel_check_in_time: b.hotel_check_in_time || calendarHotelCheckInTime || null,
          hotel_check_out_time: b.hotel_check_out_time || calendarHotelCheckOutTime || null,
          hotel_timezone: b.hotel_timezone || null,
          channel_image_path: b.channel_image_path || null,
          channel_name: b.channel_name || null,
          group_booking_id: b.group_booking_id || null,
          group_booking_code: b.group_booking_code || null,
          is_group_member: Boolean(b.is_group_member || b.group_booking_id),
        };
      });
    }
    // Track confirmed bookings to prevent duplicates
    const confirmedBookingsProcessed = new Set(); // Key: `${bookingId}_${checkInStr}_${checkOutStr}`
    
    // Phase C — bookings-only payload (no booking_items_by_date)
    if (!hasItemLevelMap && bookings && bookings.length > 0) {
      bookings.forEach(booking => {
        if (booking.id && booking.check_in_date && booking.check_out_date) {
          if (isHiddenFromCalendarBars(booking.status)) return;

          // For confirmed bookings, use a unique key to prevent duplicates
          // For bookings with room_id, use booking_id + room_id
          // For confirmed bookings without room_id, use booking_id + dates
          const checkInStr = formatDateString(booking.check_in_date);
          const checkOutStr = formatDateString(booking.check_out_date);
          const confirmedKey = createBookingKey(booking.id, checkInStr, checkOutStr);
          
          // Skip if this confirmed booking has already been processed
          if (booking.status === 'confirmed' && !booking.room_id && confirmedBookingsProcessed.has(confirmedKey)) {
            return;
          }
          
          // For bookings array, we need to find which rooms are allocated
          // This is a fallback - booking_items_by_date is preferred
          const bookingKey = booking.room_id ? `booking_${booking.id}_${booking.room_id}` : `booking_${booking.id}`;
          if (!bookingMap[bookingKey]) {
            bookingMap[bookingKey] = {
              booking: {
                id: booking.id,
                guest_name: booking.guest_name || booking.guest?.name || 'Guest',
                booking_code: booking.code || booking.booking_code || '',
                status: booking.status,
                booking_source: booking.booking_source || 'direct', // Reservation, Walk-In, etc.
                room_type_id: booking.room_type_id || null, // Room type for confirmed bookings
                channel_image_path: booking.channel_image_path || null,
                channel_name: booking.channel_name || null,
                group_booking_id: booking.group_booking_id || null,
                group_booking_code: booking.group_booking_code || null,
                is_group_member: Boolean(booking.is_group_member || booking.group_booking_id),
              },
              roomId: booking.room_id || null, // May not have room_id in bookings array
              checkIn: moment(booking.check_in_date),
              checkOut: moment(booking.check_out_date),
              status: booking.status || 'booked',
              status_color: booking.status_color || 'orange',
              blocksRoomForAllocation: true,
            };
            
            // Mark confirmed booking as processed
            if (booking.status === 'confirmed' && !booking.room_id) {
              confirmedBookingsProcessed.add(confirmedKey);
            }
          }
        }
      });
    }
    
    // Phase A — nightly allocation map
    if (booking_items_by_date) {
      Object.keys(booking_items_by_date).forEach(dateStr => {
        const dateData = booking_items_by_date[dateStr];
        
        Object.keys(dateData).forEach(roomTypeId => {
          const roomTypeData = dateData[roomTypeId];
          Object.keys(roomTypeData).forEach(roomId => {
            const bookingItems = roomTypeData[roomId];
            const visibleBookingItems = [...(bookingItems || [])];
            if (visibleBookingItems.length > 0) {
              const roomIdNum = parseInt(roomId);
              const hasAllocatedRoom = Number.isFinite(roomIdNum);
              // Process every visible item for this room/date so overlapping stays (same room,
              // same night, different bookings) each get a bar; assignBarLanes stacks them.
              const itemsToProcess = [...visibleBookingItems].sort((a, b) => {
                const aPriority = getBookingStatusPriority(a?.status, a?.item_status);
                const bPriority = getBookingStatusPriority(b?.status, b?.item_status);
                return bPriority - aPriority;
              });
              
              // Process each booking item to ensure we capture all bookings with their own dates
              itemsToProcess.forEach((item, idx) => {
                // Include all bookings - they should display according to their dates
                // Check if booking has valid dates, if not use the date from the current iteration
                if (item.booking_id) {
                  const checkInDate = dateStr;
                  const checkOutDate = moment(dateStr).add(1, 'day').format('YYYY-MM-DD');

                  if (checkInDate && checkOutDate) {
                  const bookingId = item.booking_id;
                  const checkInStr = formatDateString(checkInDate);
                  const checkOutStr = formatDateString(checkOutDate);
                  const confirmedKey = createBookingKey(bookingId, checkInStr, checkOutStr);
                  
                  // For confirmed bookings without room allocation, check if already processed
                  // This prevents the same confirmed booking from appearing multiple times
                  if (item.status === 'confirmed' && !item.booking_item_id && confirmedBookingsProcessed.has(confirmedKey)) {
                    return; // Skip duplicate confirmed booking
                  }

                  const bookingIdNum = Number(bookingId);
                  const meta = bookingMetaById[bookingIdNum] || {};
                  const bookingLevelStatus = normalizeCalendarBookingStatus(
                    meta.status || item.status || item.booking_status || 'booked',
                  );
                  if (isHiddenFromCalendarBars(bookingLevelStatus) || isHiddenFromCalendarBars(item.status)) {
                    return;
                  }
                  // Allocated: one merged bar per booking + physical room.
                  // Unallocated: one stream per (booking, room_type, room_sequence) — NOT per
                  // booking_item_id. Nightly items for the same logical room must merge into one
                  // stay bar; keying by item id painted a 1-room N-night booking on N rooms.
                  const roomSequence = item.room_sequence || 1;
                  const bookingKey = hasAllocatedRoom
                    ? `item_room_${bookingId}_${roomIdNum}`
                    : `item_unallocated_${bookingId}_${roomTypeId}_${roomSequence}`;
                  const barCheckOut = moment(checkOutDate);
                  const barCheckIn = moment(checkInDate);

                  const clampFromBookingDates = (entry) => {
                    const ci = meta.check_in_date?.isValid?.()
                      ? meta.check_in_date
                      : item.check_in_date
                        ? moment(item.check_in_date)
                        : null;
                    const co = meta.check_out_date?.isValid?.()
                      ? meta.check_out_date
                      : item.check_out_date
                        ? moment(item.check_out_date)
                        : null;
                    // Checked-out: nightly rows are often truncated after departure. Extend the
                    // bar end to booking CO so date-range Out nights stay complete. Never pull
                    // start earlier (that breaks mid-stay room moves).
                    if (bookingLevelStatus === 'checked_out') {
                      if (co && co.isValid() && co.isAfter(entry.checkOut, 'day')) {
                        entry.checkOut = co.clone();
                      }
                      const sched = scheduledCheckOutByBookingId[bookingIdNum];
                      if (sched && sched.isValid() && sched.isAfter(entry.checkOut, 'day')) {
                        entry.checkOut = sched.clone();
                      }
                      return;
                    }
                    if (ci && ci.isValid() && ci.isBefore(entry.checkIn, 'day')) {
                      entry.checkIn = ci.clone();
                    }
                    if (co && co.isValid() && co.isAfter(entry.checkOut, 'day')) {
                      entry.checkOut = co.clone();
                    }
                    const sched = scheduledCheckOutByBookingId[bookingIdNum];
                    if (sched && sched.isValid() && sched.isAfter(entry.checkOut, 'day')) {
                      entry.checkOut = sched.clone();
                    }
                  };

                  if (!bookingMap[bookingKey]) {
                    bookingMap[bookingKey] = {
                      booking: {
                        id: bookingId,
                        guest_name: item.guest_name || 'Guest',
                        booking_code: item.booking_code || '',
                        status: bookingLevelStatus,
                        booking_source: item.booking_source || 'direct', // Reservation, Walk-In, etc.
                        room_type_id: parseInt(roomTypeId) || null, // Room type for confirmed bookings
                        check_in_time: item.check_in_time || meta.check_in_time || null,
                        check_out_time: item.check_out_time || meta.check_out_time || null,
                        hotel_timezone: meta.hotel_timezone || null,
                        channel_image_path:
                          item.channel_image_path || meta.channel_image_path || null,
                        channel_name: item.channel_name || meta.channel_name || null,
                        group_booking_id:
                          item.group_booking_id || meta.group_booking_id || null,
                        group_booking_code:
                          item.group_booking_code || meta.group_booking_code || null,
                        is_group_member: Boolean(
                          item.is_group_member
                          || item.group_booking_id
                          || meta.is_group_member
                          || meta.group_booking_id
                        ),
                      },
                      roomId: hasAllocatedRoom ? roomIdNum : null,
                      checkIn: barCheckIn,
                      checkOut: barCheckOut,
                      status: bookingLevelStatus,
                      // Bar paint uses resolveRoomBarStatus(item checked_in); keep orange default
                      // until list sync. Per-night gray item.status_color is not used for spans.
                      status_color: item.status_color || meta.status_color || 'orange',
                      // Room-night vacated (checkout=true): allow another confirmed stay on this room (partial checkout).
                      blocksRoomForAllocation: item.checkout !== true,
                      segmentStartedAt: null,
                      segmentEndedAt: null,
                    };
                    mergeItemCheckinIntoEntry(bookingMap[bookingKey], item);
                    mergeSegmentTimestampsIntoEntry(bookingMap[bookingKey], item);
                    clampFromBookingDates(bookingMap[bookingKey]);

                    // Mark confirmed booking as processed to prevent duplicates
                    if (item.status === 'confirmed') {
                      confirmedBookingsProcessed.add(confirmedKey);
                    }
                  } else {
                    // Extend the segment by item dates; merge per-room check-in flags across nights.
                    if (barCheckIn.isBefore(bookingMap[bookingKey].checkIn, 'day')) {
                      bookingMap[bookingKey].checkIn = barCheckIn;
                    }
                    if (barCheckOut.isAfter(bookingMap[bookingKey].checkOut, 'day')) {
                      bookingMap[bookingKey].checkOut = barCheckOut;
                    }
                    mergeItemCheckinIntoEntry(bookingMap[bookingKey], item);
                    mergeSegmentTimestampsIntoEntry(bookingMap[bookingKey], item);
                    clampFromBookingDates(bookingMap[bookingKey]);
                    bookingMap[bookingKey].status = bookingLevelStatus;
                    if (item.status_color && bookingMap[bookingKey].item_status === 'checked_in') {
                      bookingMap[bookingKey].status_color = item.status_color;
                    } else if (!bookingMap[bookingKey].hasItemLevelCheckinInfo) {
                      bookingMap[bookingKey].status_color =
                        meta.status_color || bookingMap[bookingKey].status_color;
                    }
                    bookingMap[bookingKey].booking = {
                      ...bookingMap[bookingKey].booking,
                      status: bookingLevelStatus,
                      channel_image_path:
                        bookingMap[bookingKey].booking.channel_image_path ||
                        item.channel_image_path ||
                        meta.channel_image_path ||
                        null,
                      channel_name:
                        bookingMap[bookingKey].booking.channel_name ||
                        item.channel_name ||
                        meta.channel_name ||
                        null,
                      group_booking_id:
                        bookingMap[bookingKey].booking.group_booking_id ||
                        item.group_booking_id ||
                        meta.group_booking_id ||
                        null,
                      group_booking_code:
                        bookingMap[bookingKey].booking.group_booking_code ||
                        item.group_booking_code ||
                        meta.group_booking_code ||
                        null,
                      is_group_member: Boolean(
                        bookingMap[bookingKey].booking.is_group_member
                        || bookingMap[bookingKey].booking.group_booking_id
                        || item.is_group_member
                        || item.group_booking_id
                        || meta.is_group_member
                        || meta.group_booking_id
                      ),
                    };
                    if (item.checkout !== true) {
                      bookingMap[bookingKey].blocksRoomForAllocation = true;
                    }
                  }
                  }
                }
              });
            }
          });
        });
      });
    }

    // Phase B — always merge top-level bookings list when present (runs after Phase A so gaps fill).
    mergeBookingsApiIntoBookingMap(bookingMap, bookings, dateRange, bookingMetaById);
    // Phase B′ — list payload is source of truth for booking-level status (confirmed, partial_*, etc.).
    applyBookingListStatusToMap(bookingMap, bookings);

    // Checked-out: align bars to actual check-in / check-out (hotel TZ) and set day fractions.
    // Only the departing room (latest segment) extends into a late actual checkout day —
    // earlier rooms in a room-move keep their item-based end.
    const bookingsById = {};
    (bookings || []).forEach((b) => {
      const idNum = Number(b.id);
      if (Number.isFinite(idNum) && idNum > 0) bookingsById[idNum] = b;
    });
    const roomNumberById = {};
    const roomTypeIdByRoomId = {};
    (rooms || []).forEach((r) => {
      const idNum = Number(r?.id);
      if (!Number.isFinite(idNum) || idNum <= 0) return;
      roomNumberById[idNum] = r.number || r.room_number || r.name || String(idNum);
      const rtId = Number(r?.room_type_id ?? r?.room_type?.id ?? 0);
      if (rtId > 0) roomTypeIdByRoomId[idNum] = rtId;
    });
    const sortOrderByTypeId = buildRoomTypeSortOrderMap(room_types);
    const roomTypeNameById = buildRoomTypeNameMap(room_types);
    const entriesByBookingId = {};
    Object.values(bookingMap).forEach((entry) => {
      const idNum = Number(entry.booking?.id);
      if (!Number.isFinite(idNum) || idNum <= 0) return;
      if (!entriesByBookingId[idNum]) entriesByBookingId[idNum] = [];
      entriesByBookingId[idNum].push(entry);
    });
    // Fallback arrival/departing when room_segments missing: earliest/latest among map entries.
    const departingEntryByBookingId = {};
    const arrivalEntryByBookingId = {};
    Object.entries(entriesByBookingId).forEach(([id, entries]) => {
      let departing = null;
      let arrival = null;
      entries.forEach((entry) => {
        if (!departing) departing = entry;
        else {
          const laterOut = entry.checkOut?.isAfter?.(departing.checkOut, 'day');
          const sameOutLaterIn =
            entry.checkOut?.isSame?.(departing.checkOut, 'day')
            && entry.checkIn?.isAfter?.(departing.checkIn, 'day');
          if (laterOut || sameOutLaterIn) departing = entry;
        }
        if (!arrival) arrival = entry;
        else {
          const earlierIn = entry.checkIn?.isBefore?.(arrival.checkIn, 'day');
          const sameInEarlierOut =
            entry.checkIn?.isSame?.(arrival.checkIn, 'day')
            && entry.checkOut?.isBefore?.(arrival.checkOut, 'day');
          if (earlierIn || sameInEarlierOut) arrival = entry;
        }
      });
      departingEntryByBookingId[id] = departing;
      arrivalEntryByBookingId[id] = arrival;
    });
    Object.values(bookingMap).forEach((entry) => {
      const idNum = Number(entry.booking?.id);
      const listBooking = (Number.isFinite(idNum) && bookingsById[idNum]) || entry.booking || {};
      const meta = (Number.isFinite(idNum) && bookingMetaById[idNum]) || {};
      entry.booking = {
        ...entry.booking,
        status: entry.status || entry.booking?.status || listBooking.status,
        check_in_date:
          entry.booking?.check_in_date
          || listBooking.check_in_date
          || meta.check_in_date?.format?.('YYYY-MM-DD')
          || meta.check_in_date
          || null,
        check_out_date:
          entry.booking?.check_out_date
          || listBooking.check_out_date
          || meta.check_out_date?.format?.('YYYY-MM-DD')
          || meta.check_out_date
          || null,
        scheduled_check_out_date:
          entry.booking?.scheduled_check_out_date
          || listBooking.scheduled_check_out_date
          || meta.scheduled_check_out_date
          || listBooking.check_out_date
          || null,
        check_in_time:
          entry.booking?.check_in_time
          || listBooking.check_in_time
          || meta.check_in_time
          || null,
        check_out_time:
          entry.booking?.check_out_time
          || listBooking.check_out_time
          || meta.check_out_time
          || null,
        hotel_check_in_time:
          entry.booking?.hotel_check_in_time
          || listBooking.hotel_check_in_time
          || meta.hotel_check_in_time
          || calendarHotelCheckInTime
          || null,
        hotel_check_out_time:
          entry.booking?.hotel_check_out_time
          || listBooking.hotel_check_out_time
          || meta.hotel_check_out_time
          || calendarHotelCheckOutTime
          || null,
        hotel_timezone:
          entry.booking?.hotel_timezone
          || listBooking.hotel_timezone
          || meta.hotel_timezone
          || null,
      };

      // Prefer API room_segments so mid-stay room B is never treated as arrival when A is off-screen.
      const segmentRoles = resolveBookingRoomSegmentRoles(
        listBooking.room_segments,
        entry.roomId,
      );
      const hasApiSegments = Array.isArray(listBooking.room_segments)
        && listBooking.room_segments.length > 0;
      const isArrivalRoom = hasApiSegments
        ? segmentRoles.isArrivalRoom
        : (!Number.isFinite(idNum) || arrivalEntryByBookingId[idNum] === entry);
      const isDepartingRoom = hasApiSegments
        ? segmentRoles.isDepartingRoom
        : (!Number.isFinite(idNum) || departingEntryByBookingId[idNum] === entry);

      const segFromApi = segmentRoles.segment;
      // Only pass API segment window as a hard clip — do not invent dates from the bar itself
      // (that forced every stay through the clip path and hid missing-segment bugs).
      const bounds = resolveCheckedOutBarBounds(
        entry.booking,
        entry.checkIn,
        entry.checkOut,
        {
          isDepartingRoom,
          isArrivalRoom,
          segmentStartedAt:
            entry.segmentStartedAt?.toISOString?.()
            || entry.segmentStartedAt
            || segFromApi?.segment_started_at
            || null,
          segmentEndedAt:
            entry.segmentEndedAt?.toISOString?.()
            || entry.segmentEndedAt
            || segFromApi?.segment_ended_at
            || null,
          segmentStartDate: segFromApi?.start_date || null,
          segmentEndDate: segFromApi?.end_date || null,
        },
      );
      entry.checkIn = bounds.checkIn;
      entry.checkOut = bounds.checkOut;
      entry.startDayFraction = bounds.startDayFraction;
      entry.endDayFraction = bounds.endDayFraction;
    });

    /** Ultra-style: vacant if no overlap with stays that still hold the room (honours item checkout=true). */
    const roomHasBlockingOverlap = (roomId, checkIn, checkOut) => {
      const bars = bookingBars[roomId] || [];
      return bars.some((bar) => {
        if (bar.blocksRoomForAllocation === false) return false;
        if (!isBlockingBookingStatus(bar.status)) return false;
        return dateRangesOverlap(checkIn, checkOut, bar.startDate, bar.endDate);
      });
    };

    const allocateUnallocatedRoom = (roomTypeId, checkIn, checkOut) => {
      const roomsOfType = sortRoomsByNumber(getRoomsByType(rooms, roomTypeId));
      for (const room of roomsOfType) {
        if (!roomHasBlockingOverlap(room.id, checkIn, checkOut)) {
          return room.id;
        }
      }
      return null;
    };
    
    // Track which confirmed bookings have already been allocated (to prevent duplicates)
    const allocatedUnallocatedBookings = new Set(); // Key: `${bookingId}_${checkInStr}_${checkOutStr}`
    
    // Create booking bars from collected bookings
    // Process bookings with room allocation first, then handle unallocated confirmed bookings
    const bookingsWithRooms = [];
    const unallocatedBookingsWithoutRooms = [];
    
    // Separate bookings into two groups (use map key so multi-room unallocated streams each allocate once).
    Object.entries(bookingMap).forEach(([mapKey, bookingInfo]) => {
      if (
        isHiddenFromCalendarBars(bookingInfo.status)
        || isHiddenFromCalendarBars(bookingInfo.booking?.status)
      ) {
        return;
      }
      if (!bookingInfo.roomId && bookingInfo.booking.room_type_id) {
        unallocatedBookingsWithoutRooms.push({ ...bookingInfo, _gridStreamKey: mapKey });
      } else {
        bookingsWithRooms.push(bookingInfo);
      }
    });
    
    /** Room-wise bar status from BookingItem.checked_in / item_status (Grid card parity). */
    const barStatusForGrid = (bookingInfo, listStatus) =>
      resolveRoomBarStatus(bookingInfo, listStatus);

    // First, process bookings that already have room allocation
    bookingsWithRooms.forEach(bookingInfo => {
      const {
        booking,
        roomId,
        checkIn,
        checkOut,
        status,
        status_color,
        startDayFraction,
        endDayFraction,
      } = bookingInfo;
      
      if (!roomId) return; // Skip if no room ID
      
      // Get calendar start date (first date in range)
      const calendarStartDate = dateRange.length > 0 ? moment(dateRange[0].key) : null;
      
      // Stay began before the visible range → flush bar to calendar left (ignore CI clock).
      const startsBeforeCalendar = Boolean(
        calendarStartDate && checkIn.isBefore(calendarStartDate, 'day'),
      );
      
      // Find start and end columns using utility functions
      // Pass checkIn date to determine if same-day booking (for inclusive vs exclusive checkout)
      const startCol = findDateColumn(dateRange, checkIn, startsBeforeCalendar);
      const { endCol, extendsBeyondCalendar } = findEndDateColumn(dateRange, checkOut, checkIn);
      
      // Only create bars for bookings with valid roomId and endCol
      if (startCol >= 0 && endCol >= 0 && startCol <= endCol) {
        if (!bookingBars[roomId]) {
          bookingBars[roomId] = [];
        }
        
        // Validate: Check for duplicate booking on the same room with same date range
        if (!isDuplicateBooking(bookingBars[roomId], booking.id, checkIn, checkOut)) {
          const barStatus = barStatusForGrid(bookingInfo, status);
          const listBooking = bookingsById[Number(booking?.id)] || booking;
          bookingBars[roomId].push({
            booking,
            startDate: checkIn,
            endDate: checkOut,
            startCol,
            span: endCol - startCol + 1,
            status: barStatus,
            status_color,
            startsBeforeCalendar,
            extendsBeyondCalendar,
            // Off-screen CI time must not inset the first visible column.
            startDayFraction: startsBeforeCalendar ? null : (startDayFraction ?? null),
            endDayFraction: endDayFraction ?? null,
            blocksRoomForAllocation: bookingInfo.blocksRoomForAllocation !== false,
            roomMove: buildBarRoomMoveMeta(
              listBooking,
              roomId,
              roomNumberById,
              bookingInfo,
              sortOrderByTypeId,
              roomTypeIdByRoomId,
              roomTypeNameById,
            ),
          });
        }
      }
    });

    // Active web check-in holds: paint stay-range bars before unallocated placement
    // so held rooms are not used for provisional confirmed-booking allocation.
    const calendarStartDateForHolds = dateRange.length > 0 ? moment(dateRange[0].key).startOf('day') : null;
    const calendarEndDateForHolds =
      dateRange.length > 0 ? moment(dateRange[dateRange.length - 1].key).startOf('day') : null;
    (rooms || []).forEach((room) => {
      const invite = room?.active_web_checkin;
      if (!invite?.id || !invite.check_in_date || !invite.check_out_date) return;
      if (invite.expires_at && moment(invite.expires_at).isSameOrBefore(moment())) return;

      const checkIn = moment(invite.check_in_date).startOf('day');
      const checkOut = moment(invite.check_out_date).startOf('day');
      if (!checkIn.isValid() || !checkOut.isValid() || !checkOut.isAfter(checkIn, 'day')) return;

      // Stay nights are [checkIn, checkOut). Skip invites that do not overlap the visible week
      // (otherwise findDateColumn used to clamp future stays onto column 0 → false past bars).
      if (
        !calendarStartDateForHolds
        || !calendarEndDateForHolds
        || checkOut.isSameOrBefore(calendarStartDateForHolds, 'day')
        || checkIn.isAfter(calendarEndDateForHolds, 'day')
      ) {
        return;
      }

      const startsBeforeCalendar = checkIn.isBefore(calendarStartDateForHolds, 'day');
      const startCol = findDateColumn(dateRange, checkIn, startsBeforeCalendar);
      const { endCol, extendsBeyondCalendar } = findEndDateColumn(dateRange, checkOut, checkIn);
      if (startCol < 0 || endCol < 0 || startCol > endCol) return;

      const roomId = room.id;
      if (!bookingBars[roomId]) bookingBars[roomId] = [];
      const alreadyHasHold = bookingBars[roomId].some(
        (bar) => bar.webCheckInInvite?.id === invite.id,
      );
      if (alreadyHasHold) return;

      bookingBars[roomId].push({
        booking: null,
        webCheckInInvite: invite,
        startDate: checkIn,
        endDate: checkOut,
        startCol,
        span: endCol - startCol + 1,
        status: 'web_checkin',
        status_color: 'teal',
        startsBeforeCalendar,
        extendsBeyondCalendar,
        blocksRoomForAllocation: true,
      });
    });
    
    // Then, process unallocated bookings without room allocation
    // Sort by priority first, then booking ID and dates for stable allocation
    unallocatedBookingsWithoutRooms.sort((a, b) => {
      const priorityDelta =
        getBookingStatusPriority(b.status, b.booking?.item_status) -
        getBookingStatusPriority(a.status, a.booking?.item_status);
      if (priorityDelta !== 0) {
        return priorityDelta;
      }
      if (a.booking.id !== b.booking.id) {
        return a.booking.id - b.booking.id;
      }
      const aCheckIn = formatDateString(a.checkIn);
      const bCheckIn = formatDateString(b.checkIn);
      if (aCheckIn !== bCheckIn) {
        return aCheckIn.localeCompare(bCheckIn);
      }
      return formatDateString(a.checkOut).localeCompare(formatDateString(b.checkOut));
    });
    
    unallocatedBookingsWithoutRooms.forEach(bookingInfo => {
      const {
        booking,
        checkIn,
        checkOut,
        status,
        status_color,
        startDayFraction,
        endDayFraction,
      } = bookingInfo;
      
      const checkInStr = formatDateString(checkIn);
      const checkOutStr = formatDateString(checkOut);
      const bookingKey = createBookingKey(booking.id, checkInStr, checkOutStr);
      const streamAllocKey = `${bookingKey}__${bookingInfo._gridStreamKey || '_'}`;

      // One physical room per map row (same booking id + dates may appear multiple streams for total_rooms).
      if (allocatedUnallocatedBookings.has(streamAllocKey)) {
        return;
      }
      
      // Find first available room for this booking's room type and date range
      const allocatedRoomId = allocateUnallocatedRoom(
        booking.room_type_id,
        checkIn,
        checkOut,
      );
      
      if (allocatedRoomId) {
        // Get calendar start date (first date in range)
        const calendarStartDate = dateRange.length > 0 ? moment(dateRange[0].key) : null;
        
        const startsBeforeCalendar = Boolean(
          calendarStartDate && checkIn.isBefore(calendarStartDate, 'day'),
        );
        
        // Find start and end columns using utility functions
        // Pass checkIn date to determine if same-day booking (for inclusive vs exclusive checkout)
        const startCol = findDateColumn(dateRange, checkIn, startsBeforeCalendar);
        const { endCol, extendsBeyondCalendar } = findEndDateColumn(dateRange, checkOut, checkIn);
        
        // Only create bars for bookings with valid endCol
        if (startCol >= 0 && endCol >= 0 && startCol <= endCol) {
          if (!bookingBars[allocatedRoomId]) {
            bookingBars[allocatedRoomId] = [];
          }
          
          // Validate: Check for duplicate booking on the same room with same date range
          if (!isDuplicateBooking(bookingBars[allocatedRoomId], booking.id, checkIn, checkOut)) {
            const barStatus = barStatusForGrid(bookingInfo, status);
            const listBooking = bookingsById[Number(booking?.id)] || booking;
            bookingBars[allocatedRoomId].push({
              booking,
              startDate: checkIn,
              endDate: checkOut,
              startCol,
              span: endCol - startCol + 1,
              status: barStatus,
              status_color,
              startsBeforeCalendar,
              extendsBeyondCalendar,
              startDayFraction: startsBeforeCalendar ? null : (startDayFraction ?? null),
              endDayFraction: endDayFraction ?? null,
              blocksRoomForAllocation: bookingInfo.blocksRoomForAllocation !== false,
              roomMove: buildBarRoomMoveMeta(
                listBooking,
                allocatedRoomId,
                roomNumberById,
                bookingInfo,
                sortOrderByTypeId,
                roomTypeIdByRoomId,
                roomTypeNameById,
              ),
            });
            
            allocatedUnallocatedBookings.add(streamAllocKey);
          }
        }
      }
    });

    // Process blocked rooms from blocked_rooms_by_date
    // Structure: blocked_rooms_by_date[date][room_type_id] = {
    //   num_rooms, block_type, reason, original_start_date, original_end_date,
    //   start_time, end_time, blocks: [{ id, room_ids, start_time, end_time, ... }]
    // }
    if (blocked_rooms_by_date) {
      const calendarStartDate = dateRange.length > 0 ? moment(dateRange[0].key) : null;
      // Deduplicate by inventory-block id (or stable fallback) — avoids ghost stacked bars.
      const processedBlocks = new Set();

      Object.keys(blocked_rooms_by_date).forEach((dateStr) => {
        const dateData = blocked_rooms_by_date[dateStr];

        Object.keys(dateData).forEach((roomTypeId) => {
          const aggregate = dateData[roomTypeId];
          const roomTypeIdNum = parseInt(roomTypeId, 10);
          const blockEntries =
            Array.isArray(aggregate?.blocks) && aggregate.blocks.length > 0
              ? aggregate.blocks
              : [aggregate];

          blockEntries.forEach((blockInfo) => {
            if (!blockInfo) return;
            const numBlockedRooms = blockInfo.num_rooms || 0;
            const explicitRoomIds = Array.isArray(blockInfo.room_ids)
              ? [...new Set(blockInfo.room_ids.map((id) => Number(id)).filter(Boolean))].sort(
                  (a, b) => a - b,
                )
              : [];

            if (numBlockedRooms <= 0 && explicitRoomIds.length === 0) return;

            const originalStartDate = blockInfo.original_start_date || dateStr;
            const originalEndDate = blockInfo.original_end_date || dateStr;
            const blockKey = blockInfo.id
              ? `block_${blockInfo.id}`
              : `${roomTypeIdNum}_${originalStartDate}_${originalEndDate}_${explicitRoomIds.join(',')}_${blockInfo.start_time || ''}_${blockInfo.end_time || ''}`;
            if (processedBlocks.has(blockKey)) return;
            processedBlocks.add(blockKey);

            // original_end_date is the unblock date (exclusive), same as booking checkout.
            const blockStartDate = moment(originalStartDate).startOf('day');
            const blockEndExclusive = moment(originalEndDate).startOf('day');
            if (!blockStartDate.isValid() || !blockEndExclusive.isValid()) return;
            if (!blockEndExclusive.isAfter(blockStartDate, 'day')) return;

            const startDayFraction = timeStringToDayFraction(blockInfo.start_time);
            const endDayFraction = timeStringToDayFraction(blockInfo.end_time);

            const startsBeforeCalendar =
              calendarStartDate && blockStartDate.isBefore(calendarStartDate, 'day');

            const startCol = findDateColumn(dateRange, blockStartDate, startsBeforeCalendar);
            const { endCol, extendsBeyondCalendar } = findEndDateColumn(
              dateRange,
              blockEndExclusive,
              blockStartDate,
            );

            if (startCol < 0 || endCol < 0 || startCol > endCol) return;

            const span = endCol - startCol + 1;
            const roomsOfType = getRoomsByType(rooms, roomTypeIdNum);

            // Prefer explicit rooms; otherwise fill rooms without a hard night conflict.
            // Same-day checkout (morning) may share the day with a later block — still allow.
            const availableRooms = roomsOfType.filter((room) => {
              const hasHardConflict =
                bookingBars[room.id] &&
                bookingBars[room.id].some((bar) => {
                  if (bar.blocksRoomForAllocation === false) return false;
                  if (bar.status === 'blocked') return false;
                  if (!stayNightsOverlap(blockStartDate, blockEndExclusive, bar.startDate, bar.endDate)) {
                    return false;
                  }
                  // Same single night: checkout ending before block start is OK.
                  const barEndF =
                    bar.endDayFraction != null && Number.isFinite(Number(bar.endDayFraction))
                      ? Number(bar.endDayFraction)
                      : 0.5;
                  const blockStartF = startDayFraction != null ? startDayFraction : 0.5;
                  const barStart = moment(bar.startDate).startOf('day');
                  const barEnd = moment(bar.endDate).startOf('day');
                  const sameSingleNight =
                    barStart.isSame(blockStartDate, 'day') &&
                    barEnd.isSame(blockEndExclusive, 'day') &&
                    barEnd.diff(barStart, 'days') === 1;
                  if (sameSingleNight && barEndF <= blockStartF + 0.02) return false;
                  const status = String(bar.status || '').toLowerCase();
                  if (status === 'checked_out' && barEndF <= blockStartF + 0.02) return false;
                  return true;
                });
              return !hasHardConflict;
            });

            let roomsToBlock;
            if (explicitRoomIds.length > 0) {
              const explicitSet = new Set(explicitRoomIds);
              roomsToBlock = roomsOfType.filter((room) => explicitSet.has(Number(room.id)));
            } else {
              roomsToBlock = availableRooms.slice(0, numBlockedRooms);
            }

            roomsToBlock.forEach((room) => {
              if (!bookingBars[room.id]) {
                bookingBars[room.id] = [];
              }
              // Never paint the same inventory block twice on one room.
              if (
                blockInfo.id != null &&
                bookingBars[room.id].some(
                  (existing) =>
                    isInventoryBlockBar(existing) &&
                    Number(existing.blockInfo?.id) === Number(blockInfo.id),
                )
              ) {
                return;
              }

              bookingBars[room.id].push({
                booking: null,
                startDate: blockStartDate,
                endDate: blockEndExclusive,
                startCol,
                span,
                status: 'blocked',
                status_color: 'red',
                startDayFraction: startDayFraction ?? null,
                endDayFraction: endDayFraction ?? null,
                blockInfo: {
                  ...blockInfo,
                  reason: blockInfo.reason || 'Blocked',
                },
                startsBeforeCalendar,
                extendsBeyondCalendar,
                blocksRoomForAllocation: true,
              });
            });
          });
        });
      });

      // Same room: chain overlapping blocks so each starts after the previous (no overlay).
      Object.keys(bookingBars).forEach((roomId) => {
        layoutBlockedBarsSequentially(
          bookingBars[roomId],
          dateRange,
          calendarStartDate,
        );
      });
    }

    return { bookingBars };
  }, [
    rooms,
    room_types,
    bookings,
    booking_items_by_date,
    blocked_rooms_by_date,
    dateRange,
    calendarHotelCheckInTime,
    calendarHotelCheckOutTime,
  ]);

  const gridBookingBars = calendarGrid.bookingBars;

  // Group rooms based on groupView using utility
  const groupedRooms = useMemo(() => {
    return groupRooms(rooms, room_types, groupView);
  }, [rooms, room_types, groupView]);

  const handleBookingClick = useCallback((bookingId) => {
    if (bookingId) {
      setSelectedBookingId(bookingId);
      setDrawerOpen(true);
    }
  }, []);

  const handleWebCheckInBarClick = useCallback((invite) => {
    if (invite?.id && typeof onOpenWebCheckInInvite === 'function') {
      onOpenWebCheckInInvite(invite);
    }
  }, [onOpenWebCheckInInvite]);

  const calendarRootRef = useRef(null);

  return (
    <Box
      ref={calendarRootRef}
      sx={{
        width: '100%',
        minWidth: 0,
        position: 'relative',
        overflowX: useWorkspaceGrid ? 'hidden' : isMobileLayout ? 'hidden' : 'auto',
      }}
    >

      {/* Calendar Grid */}
      {Object.entries(groupedRooms).map(([typeId, { type, rooms: typeRooms }], sectionIndex) => {
        const sectionInventoryCounts = summarizeRoomBoardFromGridBarsRange(
          typeRooms,
          gridBookingBars,
          dateRange.length,
          dateRange.map((d) => d.key),
          currentDateStr,
        );
        return (
        <Paper
          key={typeId}
          elevation={isMobileLayout ? 0 : isDark ? 8 : 2}
          sx={{
            ...sectionPaperSx,
            ...(isMobileLayout || useWorkspaceGrid
              ? {}
              : {
                  '&:hover': {
                    boxShadow: isDark
                      ? '0 12px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(76, 225, 182, 0.15)'
                      : '0 8px 28px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.08)',
                  },
                }),
          }}
        >
          {/* Group Title with Room Count */}
          <Box
            sx={
              useWorkspaceGrid
                ? getGridSectionHeaderSx(theme)
                : {
              p: isMobileLayout ? 1 : 1.5,
              background: isMobileLayout
                ? 'rgba(255,255,255,0.03)'
                : isDark 
                  ? 'linear-gradient(135deg, #2a2a31 0%, #1e1e24 50%, #1a1a20 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f5f5f5 100%)',
              borderBottom: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(0, 0, 0, 0.08)'}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: isMobileLayout ? 0.65 : 1,
              position: 'relative',
              overflow: 'hidden',
              ...(isMobileLayout
                ? {}
                : {
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: isDark
                      ? 'inset 0 2px 4px rgba(255, 255, 255, 0.08), inset 0 -2px 4px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3)'
                      : 'inset 0 2px 4px rgba(255, 255, 255, 0.95), inset 0 -2px 4px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: isDark
                        ? 'inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4), 0 6px 20px rgba(0, 0, 0, 0.6), 0 3px 10px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.4)'
                        : 'inset 0 2px 4px rgba(255, 255, 255, 0.95), inset 0 -2px 4px rgba(0, 0, 0, 0.08), 0 6px 20px rgba(0, 0, 0, 0.15), 0 3px 10px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.08)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: isDark
                        ? 'linear-gradient(90deg, rgba(76, 225, 182, 0.6) 0%, rgba(76, 225, 182, 0.4) 25%, rgba(76, 225, 182, 0.3) 50%, rgba(76, 225, 182, 0.4) 75%, rgba(76, 225, 182, 0.6) 100%)'
                        : 'linear-gradient(90deg, rgba(76, 225, 182, 0.7) 0%, rgba(76, 225, 182, 0.5) 25%, rgba(76, 225, 182, 0.4) 50%, rgba(76, 225, 182, 0.5) 75%, rgba(76, 225, 182, 0.7) 100%)',
                      boxShadow: '0 2px 8px rgba(76, 225, 182, 0.4), 0 1px 4px rgba(76, 225, 182, 0.3)',
                      zIndex: 1,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: isDark
                        ? 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%)'
                        : 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
                      animation: 'shimmer 3s infinite',
                      zIndex: 0,
                      '@keyframes shimmer': {
                        '0%': { left: '-100%' },
                        '100%': { left: '100%' },
                      },
                    },
                  }),
            }
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 0,
                flexShrink: 0,
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                {useWorkspaceGrid ? (
                  <MeetingRoomOutlinedIcon sx={{ fontSize: 16, color: DASHBOARD_ACCENT }} />
                ) : null}
                <Typography
                  sx={
                    useWorkspaceGrid
                      ? getGridSectionTitleSx(theme)
                      : {
                    fontWeight: 700,
                    fontSize: isMobileLayout ? '0.78rem' : '0.85rem',
                    color: isDark ? '#fff' : '#212121',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    lineHeight: 1.2,
                    textShadow: isDark
                      ? '0 1px 2px rgba(0, 0, 0, 0.5), 0 1px 1px rgba(255, 255, 255, 0.1)'
                      : '0 1px 2px rgba(255, 255, 255, 0.8), 0 1px 1px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {useWorkspaceGrid ? type.name.toUpperCase() : type.name}
                </Typography>
                {isMobileLayout && mobileDateRangeLabel ? (
                  <Typography
                    sx={{
                      fontSize: '0.62rem',
                      fontWeight: 600,
                      color: 'rgba(148,163,184,0.92)',
                      letterSpacing: '0.02em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {mobileDateRangeLabel} · {dateRange.length} days
                  </Typography>
                ) : null}
              </Box>
              {!useWorkspaceGrid ? (
                <Chip
                  label={`${typeRooms.length} ${typeRooms.length === 1 ? 'Room' : 'Rooms'}`}
                  size="small"
                  sx={{
                    height: '22px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    backgroundColor: isDark
                      ? 'rgba(76, 225, 182, 0.2)'
                      : 'rgba(76, 225, 182, 0.15)',
                    color: isDark ? '#4ce1b6' : '#00a085',
                    border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                    boxShadow: isDark
                      ? 'inset 0 1px 2px rgba(255, 255, 255, 0.1), 0 1px 3px rgba(76, 225, 182, 0.3)'
                      : 'inset 0 1px 2px rgba(255, 255, 255, 0.5), 0 1px 2px rgba(76, 225, 182, 0.2)',
                    '& .MuiChip-label': {
                      padding: '0 8px',
                    },
                  }}
                />
              ) : null}
            </Box>
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                minWidth: 0,
                flex: '1 1 auto',
                display: 'flex',
                justifyContent: useWorkspaceGrid && !isMobileLayout ? 'flex-end' : 'flex-start',
              }}
            >
              <RoomBoardInventoryStats
                groupCounts={sectionInventoryCounts}
                themeMode={theme}
                dense={isMobileLayout}
                compact={!isMobileLayout && useWorkspaceGrid}
                isLoading={isLoading}
                justifyContent={isMobileLayout ? 'flex-start' : (useWorkspaceGrid ? 'flex-end' : 'flex-start')}
                rangeDays={dateRange.length}
              />
            </Box>
          </Box>

          <Box sx={{ width: '100%', minWidth: 0 }}>
          {/* Calendar Header - Dates */}
          <Box
            sx={{
              ...(useWorkspaceGrid
                ? getGridDateHeaderRowSx(theme, roomLabelWidth, dateRange.length)
                : {
              display: isMobileLayout ? 'grid' : 'flex',
              gridTemplateColumns: isMobileLayout ? mobileGridColumns(dateRange.length) : undefined,
              borderBottom: `1px solid ${isDark ? (isMobileLayout ? 'rgba(255,255,255,0.08)' : 'rgba(76, 225, 182, 0.2)') : 'rgba(0, 0, 0, 0.1)'}`,
              position: 'relative',
              overflow: 'hidden',
              background: isMobileLayout
                ? 'rgba(0,0,0,0.28)'
                : isDark 
                  ? 'linear-gradient(180deg, #2a2a31 0%, #1e1e24 50%, #1a1a20 100%)'
                  : 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 50%, #f5f5f5 100%)',
              ...(isMobileLayout
                ? {}
                : {
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: isDark
                      ? 'inset 0 2px 4px rgba(255, 255, 255, 0.08), inset 0 -2px 4px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3)'
                      : 'inset 0 2px 4px rgba(255, 255, 255, 0.95), inset 0 -2px 4px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.05)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: isDark
                        ? 'linear-gradient(90deg, rgba(76, 225, 182, 0.6) 0%, rgba(76, 225, 182, 0.4) 25%, rgba(76, 225, 182, 0.3) 50%, rgba(76, 225, 182, 0.4) 75%, rgba(76, 225, 182, 0.6) 100%)'
                        : 'linear-gradient(90deg, rgba(76, 225, 182, 0.7) 0%, rgba(76, 225, 182, 0.5) 25%, rgba(76, 225, 182, 0.4) 50%, rgba(76, 225, 182, 0.5) 75%, rgba(76, 225, 182, 0.7) 100%)',
                      boxShadow: '0 2px 8px rgba(76, 225, 182, 0.4), 0 1px 4px rgba(76, 225, 182, 0.3)',
                      zIndex: 1,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: isDark
                        ? 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%)'
                        : 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
                      animation: 'shimmer 3s infinite',
                      zIndex: 0,
                      '@keyframes shimmer': {
                        '0%': { left: '-100%' },
                        '100%': { left: '100%' },
                      },
                    },
                  }),
            }),
            }}
          >
            {/* Rooms Column Header */}
            <Box
              sx={{
                ...(useWorkspaceGrid
                  ? { minWidth: 0 }
                  : {
                width: isMobileLayout ? undefined : `${roomLabelWidth}px`,
                minWidth: isMobileLayout ? undefined : `${roomLabelWidth}px`,
                  }),
                ...(useWorkspaceGrid
                  ? getGridRoomsColumnHeaderSx(theme)
                  : {
                p: isMobileLayout ? 0.35 : 1.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                background: isMobileLayout
                  ? 'transparent'
                  : isDark 
                    ? 'linear-gradient(135deg, #2a2a31 0%, #1e1e24 100%)'
                    : 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
                borderRight: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                position: 'relative',
                ...(isMobileLayout
                  ? {}
                  : {
                      boxShadow: isDark
                        ? 'inset 0 1px 2px rgba(255, 255, 255, 0.05), 0 1px 2px rgba(0, 0, 0, 0.3)'
                        : 'inset 0 1px 2px rgba(255, 255, 255, 0.8), 0 1px 2px rgba(0, 0, 0, 0.1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: isDark
                          ? 'linear-gradient(90deg, rgba(76, 225, 182, 0.2) 0%, rgba(76, 225, 182, 0.1) 50%, rgba(76, 225, 182, 0.2) 100%)'
                          : 'linear-gradient(90deg, rgba(76, 225, 182, 0.3) 0%, rgba(76, 225, 182, 0.15) 50%, rgba(76, 225, 182, 0.3) 100%)',
                      },
                    }),
              }),
              }}
            >
              <Typography
                sx={
                  useWorkspaceGrid
                    ? getGridRoomsColumnLabelSx(theme)
                    : {
                  fontWeight: 700,
                  fontSize: isMobileLayout ? '0.52rem' : '0.7rem',
                  color: isDark ? '#ddd' : '#212121',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  lineHeight: 1.2,
                  textShadow: isDark 
                    ? '0 1px 2px rgba(0, 0, 0, 0.5)'
                    : '0 1px 1px rgba(255, 255, 255, 0.8)',
                }}
              >
                {isMobileLayout ? 'Rm' : 'Rooms'}
              </Typography>
              <Chip
                label={`${typeRooms.length}`}
                size="small"
                sx={
                  useWorkspaceGrid
                    ? getGridSectionCountChipSx(theme)
                    : {
                  height: isMobileLayout ? '16px' : '18px',
                  mt: 0.4,
                  fontSize: isMobileLayout ? '0.58rem' : '0.65rem',
                  fontWeight: 600,
                  backgroundColor: isDark 
                    ? 'rgba(76, 225, 182, 0.2)' 
                    : 'rgba(76, 225, 182, 0.15)',
                  color: isDark ? '#4ce1b6' : '#00a085',
                  border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                  boxShadow: isDark
                    ? 'inset 0 1px 2px rgba(255, 255, 255, 0.1), 0 1px 2px rgba(76, 225, 182, 0.3)'
                    : 'inset 0 1px 2px rgba(255, 255, 255, 0.5), 0 1px 2px rgba(76, 225, 182, 0.2)',
                  '& .MuiChip-label': {
                    padding: isMobileLayout ? '0 4px' : '0 5px',
                  },
                }}
              />
            </Box>
            
            {/* Date Headers */}
            {dateRange.map((dateInfo, index) => (
              <DateHeader
                key={dateInfo.key}
                dateInfo={dateInfo}
                isToday={dateInfo.key === currentDateStr}
                isDarkMode={isDark}
                isLast={index === dateRange.length - 1}
                variant={
                  isMobileLayout ? 'compact' : useWorkspaceGrid ? 'workspace' : 'default'
                }
                density={useWorkspaceGrid && effectiveDayCount > 7 ? 'compact' : 'default'}
                isWeekend={dateInfo.day === 'Sat' || dateInfo.day === 'Sun'}
                showMonthLabel={
                  isMobileLayout &&
                  (index === 0 || dateRange[index - 1]?.month !== dateInfo.month)
                }
              />
            ))}
          </Box>

          {/* Room Rows */}
          <Box
            sx={
              useWorkspaceGrid
                ? {
                    position: 'relative',
                    background: isDark
                      ? `linear-gradient(180deg, ${alpha('#ffffff', 0.015)} 0%, transparent 100%)`
                      : alpha('#000', 0.01),
                  }
                : { position: 'relative' }
            }
          >
            {typeRooms.map((room, roomIndex) => {
              const roomBars = gridBookingBars[room.id] || [];
              const barLanes = assignBarLanes(roomBars);
              const laneCount = barLanes.length ? Math.max(...barLanes) + 1 : 1;
              const rowMinHeight =
                barTopOffset + laneCount * (barHeight + barGap) + (useWorkspaceGrid ? 4 : barTopOffset);
              const rowFloor = isMobileLayout ? 36 : useWorkspaceGrid ? 26 : 46;
              const maintenanceSummary = resolveRoomMaintenanceSummary(room);
              
              return (
                <Box
                  key={room.id}
                  sx={{
                    ...(useWorkspaceGrid
                      ? getGridRowSx(theme, roomIndex >= typeRooms.length - 1, roomLabelWidth, dateRange.length)
                      : {
                    display: isMobileLayout ? 'grid' : 'flex',
                    gridTemplateColumns: isMobileLayout ? mobileGridColumns(dateRange.length) : undefined,
                    borderBottom: roomIndex < typeRooms.length - 1
                      ? `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`
                      : 'none',
                    position: 'relative',
                    overflow: 'visible',
                  }),
                    minHeight: `${Math.max(rowFloor, rowMinHeight)}px`,
                    containIntrinsicHeight: `${Math.max(rowFloor, rowMinHeight)}px`,
                  }}
                >
                  {/* Room Name Column */}
                  <Box
                    sx={{
                      ...(useWorkspaceGrid && !isMobileLayout
                        ? { minWidth: 0 }
                        : {
                      width: isMobileLayout ? undefined : `${roomLabelWidth}px`,
                      minWidth: isMobileLayout ? undefined : `${roomLabelWidth}px`,
                        }),
                      ...(useWorkspaceGrid
                        ? {
                            ...getGridRoomLabelColumnSx(theme),
                            alignSelf: 'stretch',
                            height: '100%',
                            alignItems: laneCount > 1 ? 'flex-start' : 'center',
                            pt: laneCount > 1 ? `${barTopOffset}px` : 0,
                          }
                        : {
                      p: isMobileLayout ? 0.35 : 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isMobileLayout ? 'center' : 'flex-start',
                      background: isMobileLayout ? 'rgba(255,255,255,0.02)' : isDark ? '#2a2a31' : '#ffffff',
                      borderRight: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    }),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 0.35,
                        flexWrap: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {useWorkspaceGrid && !isMobileLayout ? (
                        isLoading || room.__placeholder ? (
                          <Skeleton
                            variant="rounded"
                            animation="wave"
                            width={88}
                            height={26}
                            sx={{ borderRadius: '999px', ...calendarDataBoneSx(isDark) }}
                          />
                        ) : (
                          <Box component="span" sx={getGridRoomNumberBadgeSx(theme)}>
                            {formatRoomChipLabel(room)}
                          </Box>
                        )
                      ) : (
                        <Chip
                          label={formatRoomChipLabel(room)}
                          size="small"
                          sx={{
                            height: isMobileLayout ? '20px' : '24px',
                            fontSize: isMobileLayout ? '0.55rem' : '0.75rem',
                            fontWeight: 600,
                            maxWidth: '100%',
                            backgroundColor: isDark
                              ? 'rgba(76, 225, 182, 0.15)'
                              : 'rgba(76, 225, 182, 0.1)',
                            color: isDark ? '#4ce1b6' : '#00a085',
                            border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.2)'}`,
                            '& .MuiChip-label': {
                              padding: isMobileLayout ? '0 4px' : '0 8px',
                            },
                          }}
                        />
                      )}
                      {maintenanceSummary.hasMaintenance ? (
                        <MaintenanceRoomIndicator room={room} isDark={isDark} variant="icon" compact />
                      ) : null}
                    </Box>
                  </Box>

                  {/* Date Columns with Booking Bars */}
                  <Box
                    sx={{
                      gridColumn: isMobileLayout
                        ? `2 / span ${dateRange.length}`
                        : useWorkspaceGrid
                          ? `2 / -1`
                          : undefined,
                      flex: isMobileLayout || useWorkspaceGrid ? undefined : 1,
                      display: isMobileLayout || useWorkspaceGrid ? 'grid' : 'flex',
                      gridTemplateColumns: isMobileLayout || useWorkspaceGrid
                        ? `repeat(${dateRange.length}, minmax(0, 1fr))`
                        : undefined,
                      minWidth: 0,
                      width: isMobileLayout || useWorkspaceGrid ? '100%' : undefined,
                      position: 'relative',
                      minHeight: `${Math.max(rowFloor, rowMinHeight)}px`,
                      // Clip bars so start/end pills never bleed into the room column or past the grid.
                      overflow: 'hidden',
                    }}
                  >
                    {dateRange.map((dateInfo, colIndex) => {
                      const slotBlocked = dateColumnBlocksQuickBook(roomBars, colIndex);
                      const maintenanceBlocked = isRoomMaintenanceBlockedOn(room, dateInfo.key);
                      const canQuick =
                        !isLoading
                        && Boolean(onQuickBook)
                        && isQuickBookDateAllowed(dateInfo.key, hotelTimezone)
                        && !slotBlocked
                        && !maintenanceBlocked;
                      return (
                        <Box
                          key={dateInfo.key}
                          role={canQuick ? 'button' : undefined}
                          tabIndex={canQuick ? 0 : undefined}
                          onClick={() => {
                            if (!canQuick) return;
                            onQuickBook({
                              roomId: room.id,
                              roomNumber: room.room_number,
                              roomTypeId: room.room_type_id,
                              roomTypeName: room.room_type_name || type.name,
                              checkInDate: dateInfo.key,
                            });
                          }}
                          onKeyDown={(e) => {
                            if (!canQuick) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onQuickBook({
                                roomId: room.id,
                                roomNumber: room.room_number,
                                roomTypeId: room.room_type_id,
                                roomTypeName: room.room_type_name || type.name,
                                checkInDate: dateInfo.key,
                              });
                            }
                          }}
                          title={
                            maintenanceBlocked
                              ? getRoomMaintenanceBlockLabel(room, dateInfo.key)
                              : canQuick
                                ? 'Quick book: vacant or checked-out slot — click to open walk-in drawer'
                                : undefined
                          }
                          sx={
                            useWorkspaceGrid && !isMobileLayout
                              ? getGridDayCellSx(theme, {
                                  isWeekend: dateInfo.day === 'Sat' || dateInfo.day === 'Sun',
                                  canQuick,
                                  isToday: dateInfo.key === currentDateStr,
                                  fluid: true,
                                })
                              : {
                                  flex: isMobileLayout ? undefined : 1,
                                  minWidth: isMobileLayout ? 0 : '100px',
                                  width: isMobileLayout ? '100%' : undefined,
                                  borderRight:
                                    colIndex < dateRange.length - 1
                                      ? `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`
                                      : 'none',
                                  position: 'relative',
                                  background: isMobileLayout
                                    ? dateInfo.day === 'Sat' || dateInfo.day === 'Sun'
                                      ? 'rgba(255,255,255,0.025)'
                                      : isDark
                                        ? '#1e1e24'
                                        : '#fafafa'
                                    : isDark
                                      ? '#1e1e24'
                                      : '#fafafa',
                                  cursor: canQuick ? 'pointer' : 'default',
                                  transition: 'background-color 0.15s ease',
                                  ...(canQuick
                                    ? {
                                        '&:hover': {
                                          backgroundColor: isDark ? '#1c2430' : '#e2e8f0',
                                          boxShadow: isDark
                                            ? `inset 0 0 0 1px ${alpha(DASHBOARD_ACCENT, 0.24)}`
                                            : `inset 0 0 0 1px ${alpha(DASHBOARD_ACCENT, 0.35)}`,
                                        },
                                      }
                                    : {}),
                                }
                          }
                        >
                          {isLoading && useWorkspaceGrid && !isMobileLayout ? (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                              }}
                            >
                              <Skeleton
                                variant="rounded"
                                animation="wave"
                                width="42%"
                                height={6}
                                sx={{ borderRadius: 999, ...calendarDataBoneSx(isDark) }}
                              />
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })}

                    {/* Render Booking Bars - stacked in lanes when multiple on same room/day */}
                    {roomBars && roomBars.length > 0 && roomBars.map((bar, barIndex) => {
                      const barKey = bar.webCheckInInvite?.id
                        ? `wci-${bar.webCheckInInvite.id}`
                        : bar.blockInfo
                          ? `blk-${room.id}-${bar.blockInfo.id || 'x'}-${bar.startCol}-${bar.span}-${bar.startDayFraction ?? 'x'}-${barIndex}`
                          : `bk-${bar.booking?.id || bar.booking?.booking_id || 'x'}-${room.id}-${bar.startCol}-${bar.span}`;
                      return (
                      <BookingBar
                        key={barKey}
                        bar={bar}
                        room={room}
                        dateRangeLength={dateRange.length}
                        isDarkMode={isDark}
                        roomColumnWidth={0}
                        laneIndex={barLanes[barIndex] ?? 0}
                        barHeight={barHeight}
                        barGap={barGap}
                        barTopOffset={barTopOffset}
                        barMetrics={gridBarMetrics}
                        layoutVariant={useWorkspaceGrid ? 'workspace' : 'default'}
                        onClick={handleBookingClick}
                        onViewBooking={handleBookingClick}
                        onOpenWebCheckInInvite={handleWebCheckInBarClick}
                        onCheckIn={onCheckIn}
                        onCheckout={onCheckout}
                      />
                      );
                    })}
                    {laneCount > 1
                      ? Array.from({ length: laneCount - 1 }, (_, lane) => {
                          const top =
                            barTopOffset + (lane + 1) * (barHeight + barGap) - barGap / 2;
                          return (
                            <Box
                              key={`${room.id}-lane-line-${lane}`}
                              aria-hidden
                              sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                top: `${top}px`,
                                height: '1px',
                                bgcolor: getGridLineColor(theme),
                                pointerEvents: 'none',
                                zIndex: 2,
                              }}
                            />
                          );
                        })
                      : null}
                  </Box>
                </Box>
              );
            })}
          </Box>
          </Box>
        </Paper>
      );
      })}

      <RoomMoveLinkOverlay
        containerRef={calendarRootRef}
        isDark={isDark}
        accent={DASHBOARD_ACCENT}
      />

      {/* Booking Details Drawer */}
      <BookingDetailsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        bookingId={selectedBookingId}
      />
    </Box>
  );
};

export default memo(BookingCalendarGridView);
